import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ApiClientService} from '../../../services/api-client.service';
import {UrlService} from '../../../services/url.service';
import {Amenity, SettingsAmenity} from '../../../types/data.types';
import {AuthenticationService} from '../../../services/authentication.service';
import {AmenitiesService} from '../../../services/amenities.service';

declare const toastr: any;
declare const $: any;
declare const swal: any;
declare const moment: any;

@Component({
    selector: 'amenities-payment',
    templateUrl: './amenities-payment.component.html',
    styleUrls: ['./amenities-payment.component.scss']
})
export class AmenitiesPaymentComponent implements OnInit, OnDestroy {
    @ViewChild('form') form;
    showModal = false;
    data: Amenity = new Amenity();
    disabledSubmit = false;
    availablePaymentMethods = [];
    amenityFees = [];
    history = [];
    amenity = new SettingsAmenity();
    spinnerShow = false;
    creditCardToken: any;
    paymentMethodSelectDisabled = false;
    action = 'pay';
    chargeAmount = '';
    chargeReason = '';
    refundReason = '';
    chequeNumber = '';
    waivableFees = [];
    paymentTypeChanged = false;
    minDate = moment().add(1, 'day')._d;
    minReleaseDate = '';

    constructor(
        public api: ApiClientService,
        public amenitiesService: AmenitiesService,
        public authService: AuthenticationService,
        public urlService: UrlService
    ) {}

    ngOnInit() {
        this.initData();
        setTimeout( () => this.showModal = true, 300);
        if(this.authService.userIsResident)
            this.action = 'history';
        this.amenitiesService.onDetailedViewToggle.subscribe(() => {
            if(this.amenitiesService.selectedItemData) {
                this.data = JSON.parse(JSON.stringify(this.amenitiesService.selectedItemData));
                this.onActionChange(this.action);
            }
        });
    }

    initData() {
        this.data = JSON.parse(JSON.stringify(this.amenitiesService.selectedItemData));
        this.spinnerShow = true;
        this.action = this.isBookingDateLaterThanCurrent() ? 'history' : 'pay';
        const url = `/v1/amenity/${this.amenitiesService.selectedItemData.amenityId}.json?expand=buildingAmenity,fees`;
        this.api.get('domainAmenities', `${url}`).subscribe(
            (amenity) => {
                this.amenity = JSON.parse(amenity._body);
                if (this.amenity.paymentAcceptCash) {
                    this.availablePaymentMethods.push({value: 'cash', displayName: 'Cash'});
                }
                if (this.amenity.paymentAcceptCheques) {
                    this.availablePaymentMethods.push({value: 'cheque', displayName: 'Cheque'});
                }
                if (this.amenity.paymentAcceptCreditCards) {
                    this.availablePaymentMethods.push({value: 'credit_card', displayName: 'Credit Card'});
                }
                if(this.amenity.paymentAcceptCertifiedCheque) {
                    this.availablePaymentMethods.push({value: 'certified_cheque', displayName: 'Certified Cheque'});
                }
                if(this.amenity.paymentAcceptMoneyOrder) {
                    this.availablePaymentMethods.push({value: 'money_order', displayName: 'Money Order'});
                }
                if(this.amenity.paymentAcceptBankDraft) {
                    this.availablePaymentMethods.push({value: 'bank_draft', displayName: 'Bank Draft'});
                }
                if (this.amenity.paymentAcceptCustomType) {
                    this.availablePaymentMethods.push({value: 'custom_type', displayName: this.amenity.paymentCustomTypeName});
                }
                if(this.amenity.defaultPaymentMethod)
                    this.data.paymentMethod = this.amenity.defaultPaymentMethod;
                this.data.payments.filter((payment) => {
                    if (payment.paymentType)
                        this.data.paymentMethod = payment.paymentType;
                });
                this.formatAllFees();
                this.getPaymentsHistory();
                this.spinnerShow = false;
            }
        );
        if(this.data.customRefundableHoldDate)
            this.minReleaseDate = moment(this.data.customRefundableHoldDate).add(1, 'day')._d;
        else
            this.minReleaseDate = moment(this.data.dateTo).add(1, 'day')._d;
    }

    updateView(msg: string): void {
        toastr.success(msg);
        this.amenitiesService.getData();
        if(this.amenitiesService.view == 'calendar') {
            if (this.authService.userIsResident)
                this.amenitiesService.getAmenitiesForCalendarResident();
            else
                this.amenitiesService.getAmenitiesForCalendar();
        }
    }

    changePaymentType(event): void {
        if(event == 'credit_card')
            this.amenityFees.forEach(fee => fee.status = false);
        this.paymentTypeChanged = true;
        this.creditCardToken = null;
    }

    getTotalRefundableValue(): any {
        let returnValue = 0;
        this.amenityFees.forEach((fee) => {
            if(fee.include && fee.active && fee.refundable && !fee.waived)
                returnValue += parseFloat(fee.amount.replace(',', '.'));
        });
        return parseFloat(returnValue.toString()).toFixed(2);
    }

    getIndividualReservationProcessingFee(fee): any {
        return fee != '0.00' ? ((((parseFloat(fee) * 1.01) + 0.3) / 0.971) - parseFloat(fee)) : 0;
    }

    getIndividualTotal(fee): any {
        let total = parseFloat(fee.pmtAtTime && !fee.refundable ? (this.getStartEndDayDifference() * parseFloat(fee.amount)).toString() : fee.amount);
        return parseFloat((total + (!fee.refundable ? this.getIndividualReservationProcessingFee(total) : 0))).toFixed(2);
    }

    getReservationProcessingFeeValue(): any {
        let returnValue = 0;
        returnValue = this.getTotalPaymentValue() != '0.00' ? ((parseFloat(this.getTotalPaymentValue()) * 1.01 + 0.3) / 0.971) - parseFloat(this.getTotalPaymentValue()) : 0;
        return parseFloat(returnValue.toString()).toFixed(2);
    }

    getTotalPaymentValue(): any {
        let returnValue = 0;
        this.amenityFees.forEach((fee) => {
            if(fee.include && fee.active && !fee.refundable && !fee.waived) {
                if (!fee.pmtAtTime && !fee.pmtHourly) {
                    returnValue += parseFloat(fee.amount.replace(',', '.'));
                } else if(fee.pmtAtTime) {
                    returnValue += parseFloat(fee.amount.replace(',', '.')) * this.getStartEndDayDifference();
                } else if(fee.pmtHourly) {
                    returnValue += parseFloat(fee.amount.replace(',', '.')) * this.getStartEndHourDifference();
                }
            }
        });
        return parseFloat(returnValue.toString()).toFixed(2);
    }

    getTotalCharged(): any {
        return parseFloat((parseFloat(this.getTotalPaymentValue()) + (this.data.paymentMethod == 'credit_card' ? parseFloat(this.getReservationProcessingFeeValue()) : 0)).toString()).toFixed(2);
    }

    getPaymentsFormatted(): Array<any> {
        let formatted = [];
        this.amenityFees.forEach((fee) => {
            if(fee.include && (this.data.paymentMethod == 'credit_card' ? !fee.waived : fee.status)
               && (this.action == 'refund' ? true : !fee.originallyPaid)) {
                let total = '';
                if(fee.pmtAtTime && this.action != 'refund')
                    total = (this.getStartEndDayDifference() * parseFloat(fee.amount)).toString();
                else if(fee.pmtHourly && this.action != 'refund')
                    total = (this.getStartEndHourDifference() * parseFloat(fee.amount)).toString();
                else
                    total = fee.amount;
                let pushable = {
                    action: this.action,
                    description: this.refundReason,
                    payment: this.action == 'refund' ? fee.id : fee.paymentId,
                    amount: this.action != 'waive' ? total : undefined,
                    chequeNumber: (this.action == 'refund' && this.data.paymentMethod == 'cheque') ? this.chequeNumber : undefined
                };
                formatted.push(pushable);
            }
        });
        return formatted;
    }

    ifRequired(fee) {
        let required = false;
        this.amenityFees.forEach((amenityFee) => {
            if(amenityFee.name == fee.name)
                required = amenityFee.required;
        });
        return required;
    }

    setCard(card) {
        this.creditCardToken = card;
    }

    onStripeSuccess() {
        const data = {payments : this.formatToPatchCreditCard()};
        const url = `/v1/booking/${this.data.id}.json`;
        this.amenitiesService.api.patch('domainAmenities', url,  data).subscribe(
            () => this.onSubmit(),
            (error) => this.api.errorHandler(error, 'Failed to change card details')
        );
    }

    onStripeFailure(event) {
        if(event.code == 'empty') {
            const data = {
                payments : this.amenityFees.map(fee => ({
                               fee: fee.id,
                               chosen: fee.include ? 1 : 0,
                               refundable: fee.refundable,
                               paymentType: fee.originallyPaid ? fee.paymentType : this.data.paymentMethod,
                               name: fee.name,
                               amount: fee.amount,
                               total: this.individualPaymentTotalBasedOnType(fee)
                           })),
                isPayByLink: true
            };
            const url = `/v1/booking/${this.data.id}.json`;
            this.amenitiesService.api.patch('domainAmenities', url,  data).subscribe(
                () => this.updateView('The Payment was successfully updated'),
                (error) => this.api.errorHandler(error, 'Failed to add payment')
            );
        }
    }

    clickStripeHiddenButton() {
        document.getElementById('confirmStripeCreditCard').click();
    }

    onSubmit() {
        if(this.action === 'pay') {
            if (!this.data.paymentMethod) {
                toastr.warning('Payment method should not be blank.');
                return;
            }

            // if (this.data.paymentMethod == 'credit_card' && !this.creditCardToken) {
            //     toastr.warning('Credit Card details must be entered and applied.');
            //     return;
            // }

            if(this.getPaymentsFormatted().length < 1) {
                toastr.warning('At least one additional payment must be paid.');
                return;
            }
        } else if(this.action === 'refund') {
            if(this.amenityFees.filter(fee => fee.include).length < 1) {
                toastr.warning('You must select the fee to refund.');
                return;
            }
            if(!this.refundReason) {
                toastr.warning('You must enter the reason for refund.');
                return;
            }
        } else if(this.action === 'charge') {
            if(this.amenityFees.filter(fee => fee.include).length < 1) {
                toastr.warning('You must select the fee to charge.');
                return;
            }
            if(!this.chargeAmount) {
                toastr.warning('You must enter the charge amount.');
                return;
            }
            if(!this.chargeReason) {
                toastr.warning('You must enter the reason for charging.');
                return;
            }
            if(parseFloat(this.chargeAmount) > this.amenityFees.filter(fee => fee.include)[0].maxPenaltyAmount) {
                toastr.warning('The amount you are trying to charge is greater than held value excluding the processing fee.');
                return;
            }
        } else if(this.action === 'extend') {
            if((!this.hasHold() && !this.form.value.customRefundableHoldDate) || !this.form.value.customRefundableReleaseDate) {
                toastr.warning('All dates must be filled');
                return;
            }
        }

        let data = null;
        if(this.action == 'charge') {
            data = {
                payment : this.amenityFees.filter(fee => fee.include)[0].paymentId,
                description: this.chargeReason,
                amount: this.chargeAmount
            };
        } else if(this.action == 'waive') {
            data = {
                elements: this.data.payments.filter(p => this.waivableFees.filter(fee => fee.waived).find(f => f.fee == p.fee)).map(a => ({
                              action: 'waive',
                              payment: a.id,
                          }))
            };
        } else if(this.action == 'extend') {
            data = {
                customRefundableHoldDate:    !this.hasHold() ? moment(this.form.value.customRefundableHoldDate).format('YYYY-MM-DD') : undefined,
                customRefundableReleaseDate: moment(this.form.value.customRefundableReleaseDate).format('YYYY-MM-DD')
            };
        } else {
            data = { elements: this.getPaymentsFormatted() };
        }
        this.disabledSubmit = true;
        this.spinnerShow = true;
        let url = '';
        if(this.action != 'extend')
            url = `/v1/${this.action == 'charge' ? 'payment_penalty' : 'payments_action'}.json`;
        else
            url = `/v1/booking/${this.data.id}.json`;
        this.amenitiesService.api[this.action != 'extend' ? 'post' : 'patch']('domainAmenities', url, data).subscribe(
            () => {
                this.updateView('The Payment was successfully updated');
                this.amenitiesService.getData();
            },
            error => {
                this.api.errorHandler(error, 'Update failed!');
                this.disabledSubmit = false;
                this.spinnerShow = false;
            }
        );
    }

    removePaidStatus(event, fee) {
        if(!event)
            fee.status = 0;
    }

    getStartEndDayDifference(): number {
        return Math.round((new Date(this.data.dateTo).getTime() - new Date(this.data.dateFrom).getTime()) / (24 * 3600000));
    }

    getStartEndHourDifference(): number {
        return (+moment.utc(moment(this.data.dateTo).diff(moment(this.data.dateFrom))).format('x')) / (60 * 60 * 1000);
    }

    onActionChange(event): void {
        this.amenityFees = [];
        if(event === 'pay')
            this.formatAllFees();
        else if(event === 'refund')
            this.amenityFees = this.getPaidFees();
        else if(event === 'charge')
            this.amenityFees = this.getRefundableFees();
        else if(event === 'waive')
            this.waivableFees = this.getWaivableFees();
        else if(event === 'history')
            this.getPaymentsHistory();
        else if(event === 'extend') {
            const original = JSON.parse(JSON.stringify(this.amenitiesService.selectedItemData));
            this.data.customRefundableHoldDate = original.customRefundableHoldDate;
            this.data.customRefundableReleaseDate = original.customRefundableReleaseDate;
        }
    }

    getPaidFees() {
        let paid = [];
        if(this.amenity.fees) {
            this.data.payments.forEach((payment) => {
                if(payment.status) {
                    paid.push({
                        id: payment.id,
                        active: 1,
                        name: payment.name,
                        status: payment.status,
                        amount: payment.currentTotal.toFixed(2),
                        required: false,
                        include: false,
                        refundable: payment.refundable
                    });
                }
            });
            this.amenity.fees.forEach((fee) => {
                paid.forEach(payment => {
                    if(fee.name == payment.name) {
                        payment.paymentId = fee.id;
                        payment.pmtAtTime = fee.pmtAtTime;
                    }
                });
            });
        }
        return paid;
    }

    getRefundableFees() {
        let refundable = [];
        if(this.amenity.fees) {
            this.amenity.fees.forEach((fee) => {
                if(fee.refundable) {
                    refundable.push({
                        id: fee.id,
                        active: fee.active,
                        name: fee.name,
                        status: fee.status,
                        amount: fee.amount,
                        required: false,
                        include: false,
                        refundable: fee.refundable,
                        pmtAtTime: fee.pmtAtTime,
                        pmtHourly: fee.pmtHourly
                    });
                }
            });
            this.data.payments.forEach((payment) => {
                refundable.forEach((fee, index) => {
                    if (fee.name == payment.name) {
                        fee.paymentId = payment.id;
                        fee.penalty = payment.penalty;
                        fee.maxPenaltyAmount = payment.maxPenaltyAmount;
                        if(payment.refundable && !payment.status)
                            refundable.splice(index, 1);
                    }
                });
            });
        }
        return refundable;
    }

    getWaivableFees() {
        let refundable = [];
        let waivable = [];

        if(this.amenity.fees) {
            waivable = this.amenity.fees.filter(fee => fee.availableWaive && fee.active);
            refundable = this.data.payments.filter(p => waivable.find(a => a.id == p.fee) && !p.status);
        }

        return JSON.parse(JSON.stringify(refundable));
    }

    formatAllFees() {
        if(this.amenity.fees) {
            this.amenity.fees.forEach((fee) => {
                this.amenityFees.push({
                    id: fee.id,
                    active: fee.active,
                    name: fee.name,
                    amount: fee.amount,
                    required: fee.required,
                    include: fee.required ? true : false,
                    refundable: fee.refundable ? 1 : 0,
                    pmtAtTime: fee.pmtAtTime,
                    pmtHourly: fee.pmtHourly
                });
            });
            this.data.payments.forEach((payment) => {
                this.amenityFees.forEach((fee) => {
                    if(fee.name == payment.name)
                        fee.paymentId = payment.id;
                    if(fee.name == payment.name && fee.status != payment.status) {
                        fee.status = payment.status;
                        fee.originallyPaid = fee.status;
                        if(fee.status)
                            this.paymentMethodSelectDisabled = true;
                    }
                    if(fee.name == payment.name && fee.include != payment.chosen)
                        fee.include = payment.chosen;
                    if(fee.id == payment.fee) {
                        fee.waived = payment.waived;
                        payment.originallyWaived = payment.waived;
                    }
                });
            });
        }
    }

    getPaymentsHistory() {
        this.history = [];
        if(this.data.payments) {
            this.data.payments.forEach(payment => {
                if(payment.transactions) {
                    payment.transactions.forEach(transaction => {
                        this.history.push({
                            name: payment.name,
                            amount: transaction.amount,
                            createdBy: transaction.createdBy ? transaction.createdBy.fullName : '',
                            createdAt: transaction.createdAt,
                            description: transaction.description,
                            action: transaction.action,
                            status: transaction.status
                        });
                    });
                }
            });
            this.history.sort((a: any, b: any) => {
                if (new Date(a.createdAt) < new Date(b.createdAt)) {
                    return 1;
                } else if (new Date(a.createdAt) > new Date(b.createdAt)) {
                    return -1;
                } else {
                    return 0;
                }
            });
        }
    }

    public validateFeeValue(event) {
        return (event.charCode >= 48 && event.charCode <= 57) || event.charCode == 44 || event.charCode == 46 || event.charCode == 0;
    }

    public formatToPatchCreditCard() {
        return this.amenityFees.map(fee => ({
            fee: fee.id,
            chosen: fee.chosen,
            refundable: fee.refundable,
            paymentType: fee.originallyPaid ? fee.paymentType : this.data.paymentMethod,
            name: fee.name,
            amount: fee.amount,
            total: this.individualPaymentTotalBasedOnType(fee),
            cardToken: fee.originallyPaid ? undefined : this.creditCardToken
        }));
    }

    public formatToPatchNewPaymentMethod() {
        return this.amenityFees.map(fee => ({
            fee: fee.id,
            chosen: fee.chosen,
            refundable: fee.refundable,
            paymentType: fee.originallyPaid ? fee.paymentType : this.data.paymentMethod,
            name: fee.name,
            amount: fee.amount,
            total: this.individualPaymentTotalBasedOnType(fee),
        }));
    }

    individualPaymentTotalBasedOnType(fee) {
        if(fee.pmtAtTime)
            return (this.getStartEndDayDifference() * parseFloat(fee.amount)).toString();
        else if(fee.pmtHourly)
            return (this.getStartEndHourDifference() * parseFloat(fee.amount)).toString();
        else
            return fee.amount;
    }

    updateNewPaymentMethod() {
        if(!this.bookingHasOriginallyPaidPayments() && this.paymentTypeChanged && this.action === 'pay') {
            if(this.action === 'pay') {
                if (!this.data.paymentMethod) {
                    toastr.warning('Payment method should not be blank.');
                    return;
                }

                if (this.data.paymentMethod == 'credit_card' && !this.creditCardToken) {
                    toastr.warning('Credit Card details must be entered and applied.');
                    return;
                }

                if(this.getPaymentsFormatted().length < 1) {
                    toastr.warning('At least one additional payment must be paid.');
                    return;
                }
            } else if(this.action === 'refund') {
                if(this.amenityFees.filter(fee => fee.include).length < 1) {
                    toastr.warning('You must select the fee to refund.');
                    return;
                }
                if(!this.refundReason) {
                    toastr.warning('You must enter the reason for refund.');
                    return;
                }
            } else if(this.action === 'charge') {
                if(this.amenityFees.filter(fee => fee.include).length < 1) {
                    toastr.warning('You must select the fee to charge.');
                    return;
                }
                if(!this.chargeAmount) {
                    toastr.warning('You must enter the charge amount.');
                    return;
                }
                if(!this.chargeReason) {
                    toastr.warning('You must enter the reason for charging.');
                    return;
                }
                if(parseFloat(this.chargeAmount) > this.amenityFees.filter(fee => fee.include)[0].maxPenaltyAmount) {
                    toastr.warning('The amount you are trying to charge is greater than held value excluding the processing fee.');
                    return;
                }
            }

            const data = {payments: this.formatToPatchNewPaymentMethod()};
            const url = `/v1/booking/${this.data.id}.json`;
            this.amenitiesService.api.patch('domainAmenities', url, data).subscribe(
                () => this.onSubmit(),
                (error) => this.api.errorHandler(error, 'Failed to change payment method')
            );
        } else
            this.onSubmit();
    }

    selectCheckbox(event, index) {
        if(this.action == 'charge') {
            if (event.target.checked) {
                this.amenityFees.forEach(fee => fee.include = false);
                this.amenityFees[index].include = true;
            } else
                this.amenityFees[index].include = false;
        }
    }

    getProcessingFee() {
        return this.amenityFees.filter(fee => fee.include).length > 0 ? this.amenityFees.filter(fee => fee.include)[0].maxPenaltyAmount : '';
    }

    isBookingDateLaterThanCurrent(): boolean {
        let flag = false;
        if(new Date(this.data.dateTo.slice(0, -6)) <= new Date())
            flag = true;
        return flag;
    }

    bookingHasPaidPayments() {
        return this.amenityFees.find(f => f.status);
    }

    bookingHasOriginallyPaidPayments() {
        return this.amenityFees.some(f => f.originallyPaid);
    }

    getExpectedHoldTerm(number) {
        return moment(this.data.dateFrom).add(number, 'days').format('YYYY-MM-DD');
    }

    ngOnDestroy() {
        this.amenitiesService.onDetailedViewToggle.observers = [];
    }

    getRefundableFeesForExtending() {
        return this.data.payments.filter(f => f.refundable && !f.transactions.some(t => t.action == 'refund'));
    }

    hasHold() {
        return this.data.payments.filter(f => f.refundable && f.transactions.some(t => t.action == 'hold')).length > 0;
    }

    onStartDateChanged(event) {
        this.data.customRefundableReleaseDate = null;
        this.minReleaseDate = moment(event).add(1, 'day')._d;
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/amenities/amenity-payment/amenities-payment.component.ts